/**
 * "Which way, and how far?" — computed here, for one target at a time.
 *
 * Pulled out of the live screen because a room asks the question of everybody at once: the hero
 * arrow points at one person, and the chevrons around the rim point at the rest. One function,
 * called N times, so the person you are walking to and the person behind you can never be drawn
 * by two subtly different pieces of maths.
 */

import {
  ARROW,
  haversineDistance,
  initialBearing,
  normalizeDegrees,
  shortestAngleDelta,
  type LatLng,
} from '@beeline/shared';

import type { MemberState } from './session-model';
import type { SensorState } from '../sensors';

/** What the arrow is currently able to say. Drives both the visuals and the words. */
export interface Geometry {
  distanceMetres: number | null;
  /** Bearing to the target, degrees clockwise from true north. */
  bearing: number | null;
  /** Where to point on screen: bearing minus heading. Null without a compass. */
  relative: number | null;
  onCourse: boolean;
  /**
   * Metres the target is above you, when both devices reported an altitude and the gap is big
   * enough to matter. Null everywhere else, which is almost everywhere.
   */
  climbMetres: number | null;
}

export const NO_GEOMETRY: Geometry = {
  distanceMetres: null,
  bearing: null,
  relative: null,
  onCourse: false,
  climbMetres: null,
};

/**
 * Below this the vertical component is noise: GPS altitude is far worse than its horizontal
 * accuracy, and "2 m below you" on a flat beach would be an outright lie.
 */
const MIN_MEANINGFUL_CLIMB_M = 8;

export interface SelfPosition extends LatLng {
  usable: boolean;
  altitude?: number | null;
}

export interface TargetPosition extends LatLng {
  altitude?: number | null;
}

/**
 * Geometry from one position to another.
 *
 * `wasOnCourse` is the previous frame's answer, and passing it is what applies hysteresis to the
 * glow: it takes a wider swing to lose "on course" than it took to earn it, so somebody standing
 * still on the boundary does not watch the arrow strobe.
 */
export function geometryFor(
  self: SelfPosition | null,
  target: TargetPosition | null,
  sensors: Pick<SensorState, 'heading' | 'headingAvailable'>,
  wasOnCourse = false,
): Geometry {
  // A fix worse than a hundred metres is excluded from the computation entirely — an arrow
  // drawn from it would be confidently wrong.
  if (!self || !self.usable || !target) return NO_GEOMETRY;

  const distanceMetres = haversineDistance(self, target);
  const bearing = initialBearing(self, target);

  const climb =
    typeof self.altitude === 'number' && typeof target.altitude === 'number'
      ? target.altitude - self.altitude
      : null;
  const climbMetres = climb !== null && Math.abs(climb) >= MIN_MEANINGFUL_CLIMB_M ? climb : null;

  if (!sensors.headingAvailable || sensors.heading === null) {
    return { distanceMetres, bearing, relative: null, onCourse: false, climbMetres };
  }

  const relative = normalizeDegrees(bearing - sensors.heading);
  const offBy = Math.abs(shortestAngleDelta(0, relative));
  const threshold = wasOnCourse ? ARROW.onCourseReleaseDegrees : ARROW.onCourseDegrees;

  return { distanceMetres, bearing, relative, onCourse: offBy <= threshold, climbMetres };
}

/** One member, and where they are relative to you. What the rim and the roll call are made of. */
export interface MemberGeometry {
  member: MemberState;
  geometry: Geometry;
}

/** Geometry for a whole room, sorted nearest first — which is the order a roll call wants. */
export function geometryForAll(
  self: SelfPosition | null,
  members: readonly MemberState[],
  sensors: Pick<SensorState, 'heading' | 'headingAvailable'>,
): MemberGeometry[] {
  return members
    .map((member) => ({
      member,
      geometry: geometryFor(
        self,
        member.frame ? { ...member.frame, altitude: member.frame.alt ?? null } : null,
        sensors,
      ),
    }))
    .sort((left, right) => {
      // Anyone we cannot place sinks to the bottom rather than pretending to be at zero metres.
      const a = left.geometry.distanceMetres ?? Number.POSITIVE_INFINITY;
      const b = right.geometry.distanceMetres ?? Number.POSITIVE_INFINITY;
      return a - b;
    });
}

/** "40 m below you" — only ever called when `climbMetres` survived the threshold above. */
export function describeClimb(climbMetres: number): string {
  const magnitude = Math.round(Math.abs(climbMetres));
  return climbMetres > 0 ? `${magnitude} m above you` : `${magnitude} m below you`;
}

/**
 * How wide the arrow's honest answer is, in degrees.
 *
 * Two fixes, each with its own error, produce a bearing with an error of its own — and it grows
 * as the gap shrinks, which is precisely why the product stops pointing under thirty metres.
 * At 200 m with 10 m of combined error the bearing is good to about three degrees; at 20 m it is
 * good to about twenty-seven, which is a third of the sky.
 *
 * Drawing the arrow as a cone this wide makes that visible instead of merely stated. A confident
 * needle and a vague wedge are the same fact; only one of them is believed.
 */
export function bearingUncertaintyDegrees(
  distanceMetres: number | null,
  selfAccuracy: number | null,
  targetAccuracy: number | null,
): number | null {
  if (distanceMetres === null || distanceMetres <= 0) return null;

  // Errors add in quadrature: they are independent, so the worst case is not the sum.
  const self = selfAccuracy ?? 0;
  const target = targetAccuracy ?? 0;
  const combined = Math.hypot(self, target);
  if (combined <= 0) return 0;

  const radians = Math.atan2(combined, distanceMetres);
  // Capped at a right angle: past that the honest statement is "somewhere over there", and the
  // range layering has already switched away from an arrow anyway.
  return Math.min(90, (radians * 180) / Math.PI);
}

/**
 * How far somebody could have got since their last fix.
 *
 * A frozen arrow already desaturates and says the signal is old. This turns that into a number:
 * a stale position is not wrong, it is a circle that grows at walking pace, and after two
 * minutes it is a circle nobody should be walking into confidently.
 */
export function ghostRadiusMetres(ageMs: number, accuracyMetres: number | null): number {
  /** Brisk walking. Not running: a running estimate would make every ghost useless in seconds. */
  const WALK_MPS = 1.4;
  const travelled = Math.max(0, ageMs / 1000) * WALK_MPS;
  return Math.round(travelled + (accuracyMetres ?? 0));
}

/** "Ana was here 2 minutes ago — could be anywhere within 180 m." */
export function describeGhost(name: string | null, ageMs: number, radiusMetres: number): string {
  const who = name ?? 'They';
  const minutes = Math.round(ageMs / 60_000);
  const when =
    ageMs < 90_000 ? `${Math.round(ageMs / 1000)} seconds ago` : `${minutes} minutes ago`;

  return `${who} ${name ? 'was' : 'were'} here ${when} — could be anywhere within ${radiusMetres} m`;
}
